// Learning Value: React UI on the BoldPanelLayout template. The maths lives in ./model.js
// and src/lib/voi.js. A decision can be handed to the Learning Backlog
// (src/lib/handBack.js).

import { useMemo, useState } from "react";
import { BoldPanelLayout } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  DataTable,
  ExampleDataPicker,
  Explainer,
  Field,
  FieldGrid,
  MetricCard,
  MetricGrid,
  OpenInTool,
  Panel,
  RangeInput,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
  type Range,
} from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { hubLink, hubState, initialBack } from "@/lib/handBack.js";
import { METHODS, methodById } from "./methods.js";
import { analyseLearning, fmt, pct, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { MAX_METHODS, learningShare } from "./share.js";
import { DEFAULT_STATE as BACKLOG_DEFAULT, learnBacklogShare } from "@/learnbacklog/share.js";

const HEADING = headingFor("tool/learning-value");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface Method {
  label: string;
  preset: string;
  noise: number;
  bias: number;
  days: number;
}
interface State {
  decision: string;
  unit: string;
  dayValue: number;
  value: Range;
  methods: Method[];
}

const INITIAL = EXAMPLES[0].state as State;
const numberOr = (text: string, fallback: number) => (text.trim() === "" || !Number.isFinite(Number(text)) ? fallback : Number(text));

export function App() {
  const shared = useUrlRestore<State>(learningShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  const [back] = useState(() => initialBack(["learnbacklog"]));
  useUrlSync(learningShare, () => state, [state], shared);
  const set = (patch: Partial<State>) => setState((s) => ({ ...s, ...patch }));
  const setMethod = (i: number, patch: Partial<Method>) => setState((s) => ({ ...s, methods: s.methods.map((m, k) => (k === i ? { ...m, ...patch } : m)) }));
  const r: Any = useMemo(() => analyseLearning(state), [state]);
  const ok = !r.issues.length;
  const u = state.unit;

  const hub: Any = hubState(back, learnBacklogShare);
  const backlogHref = (() => {
    if (!ok || !state.methods.length) return undefined;
    const pick = state.methods[r.best.index];
    const base = hub ?? { ...BACKLOG_DEFAULT, unit: u, dayValue: state.dayValue };
    const question = { label: state.decision, value: { ...state.value }, preset: pick.preset, noise: pick.noise, bias: pick.bias, days: pick.days };
    // A question sent here from the backlog comes back in its place, not as a copy.
    const at = base.questions.findIndex((q: Any) => q.label === question.label);
    const questions = at >= 0 ? base.questions.map((q: Any, k: number) => (k === at ? question : q)) : [...base.questions, question].slice(-40);
    return hubLink("/tool/learning-backlog", learnBacklogShare, { ...base, questions });
  })();

  const recommendation = !ok
    ? "—"
    : r.settled
      ? r.decideNow === "build" ? "Build it now" : "Drop it now"
      : r.learn
        ? "Learn first"
        : r.decideNow === "build" ? "Build it now" : "Drop it now";

  return (
    <BoldPanelLayout
      label="Unit 39 / Learning value"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      eyebrow="Value of information"
      title={HEADING.h1}
      lede={ledeOf(HEADING)}
      footer={
        <BarFooter>
          <span>Expected value of sample information · noise and bias</span>
          <span>{ok ? `${recommendation} · perfect knowledge worth ${fmt(r.evpi)} ${u}` : "Set the value range"}</span>
        </BarFooter>
      }
    >
      <Explainer id="learning" title="What this answers" summary="Whether to learn more before building, and how" defaultOpen>
        <p>
          Learning is worth something only if it could <strong>change what you do</strong>. If every plausible
          outcome says build, build. If the call is close, a cheap study can save an expensive mistake.
        </p>
        <p>
          Each way to learn has <strong>noise</strong> (small samples, mixed signals) and <strong>bias</strong>{" "}
          (people say yes in interviews; a before/after also catches the season). More data shrinks noise, never
          bias. The page prices each method by how much it is expected to improve the decision, against its cost.
          No A/B platform needed: a rollout to accounts picked at random is a randomized test.
        </p>
      </Explainer>

      <Panel step={1} title="Start from an example" hint="Or describe your decision in 02">
        <UrlStateNotices state={shared} />
        <ExampleDataPicker
          examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
          onLoad={(id) => {
            const ex = exampleById(id);
            if (ex) setState(ex.state as State);
          }}
          deepLink={!shared.initial}
        />
        <p className="mb-0 mt-3 text-xs text-muted-foreground">Examples are illustrative: invented decisions, values and costs.</p>
      </Panel>

      <Panel step={2} title="The decision" hint="A loss is allowed at the low end">
        <FieldGrid>
          <Field label="Decision">
            <Input aria-label="Decision" value={state.decision} onChange={(e) => set({ decision: e.target.value })} />
          </Field>
          <Field label="Value unit" hint="e.g. €k, signups">
            <Input aria-label="Value unit" maxLength={20} value={u} onChange={(e) => set({ unit: e.target.value })} />
          </Field>
          <Field label="Net value if built" hint="90% range, after its build cost">
            <RangeInput label="Net value if built" value={state.value} onChange={(value) => set({ value })} unit={u} />
          </Field>
          <Field label="Value of one team-day" hint={`In ${u}; prices each method's days`}>
            <Input type="number" min={0} step="any" aria-label="Value of one team-day" value={String(state.dayValue)} onChange={(e) => set({ dayValue: Math.max(0, numberOr(e.target.value, 0)) })} />
          </Field>
        </FieldGrid>
        {!ok && (
          <Callout kind="error" className="mt-4">
            {r.issues.join(" ")}
          </Callout>
        )}
      </Panel>

      <Panel step={3} title="Ways to learn" hint="Noise and bias are starting assumptions">
        <DataTable<Method>
          rows={state.methods}
          rowKey={(_, i) => i}
          columns={[
            {
              head: "Method",
              cell: (m, i) => (
                <Select
                  aria-label={`Method ${i + 1}`}
                  value={m.preset}
                  onChange={(e) => {
                    const p = methodById(e.target.value);
                    setMethod(i, { preset: p.id, label: p.label, noise: p.noise, bias: p.bias });
                  }}
                >
                  {METHODS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              ),
            },
            {
              head: "Days",
              numeric: true,
              cell: (m, i) => (
                <Input type="number" min={0} step="any" aria-label={`Method ${i + 1} days`} className="h-8 w-20 rounded-none font-mono text-xs" value={String(m.days)} onChange={(e) => setMethod(i, { days: Math.max(0, numberOr(e.target.value, 0)) })} />
              ),
            },
            {
              head: "Noise",
              numeric: true,
              cell: (m, i) => (
                <Input type="number" min={0} max={10} step={0.1} aria-label={`Method ${i + 1} noise`} className="h-8 w-20 rounded-none font-mono text-xs" value={String(m.noise)} onChange={(e) => setMethod(i, { noise: Math.min(10, Math.max(0, numberOr(e.target.value, 0))) })} />
              ),
            },
            {
              head: "Bias",
              numeric: true,
              cell: (m, i) => (
                <Input type="number" min={0} max={10} step={0.1} aria-label={`Method ${i + 1} bias`} className="h-8 w-20 rounded-none font-mono text-xs" value={String(m.bias)} onChange={(e) => setMethod(i, { bias: Math.min(10, Math.max(0, numberOr(e.target.value, 0))) })} />
              ),
            },
            {
              head: <span className="sr-only">Remove</span>,
              cell: (m, i) => (
                <Button variant="outline" size="sm" aria-label={`Remove ${m.label}`} onClick={() => setState((s) => ({ ...s, methods: s.methods.filter((_, k) => k !== i) }))}>
                  ×
                </Button>
              ),
            },
          ]}
        />
        <Button
          className="mt-3"
          disabled={state.methods.length >= MAX_METHODS}
          onClick={() => {
            const p = methodById("interviews");
            setState((s) => ({ ...s, methods: [...s.methods, { label: p.label, preset: p.id, noise: p.noise, bias: p.bias, days: 3 }] }));
          }}
        >
          + Add a way to learn
        </Button>
        <p className="mb-0 mt-3 text-sm text-muted-foreground">
          Noise and bias are multiples of today&rsquo;s uncertainty: 1 means the method alone is about as unsure as
          you are now. Defaults are a starting point for discussion, not measurements — change them when you know
          your method better.
        </p>
      </Panel>

      {ok && (
        <Panel step={4} title="Answer" hint="Conditional on the value range and assumptions">
          <MetricGrid className="lg:grid-cols-3">
            <MetricCard accent eyebrow="Recommendation" value={recommendation} data-value={r.learn ? r.best.net : 0}>
              {r.settled
                ? "No study could change this call."
                : r.learn
                  ? `${r.best.label}: net ${fmt(r.best.net)} ${u}.`
                  : "No listed method is worth its cost."}
            </MetricCard>
            <MetricCard eyebrow="Chance building is worth it" value={pct(r.chanceWorthIt)}>
              Chance the net value is above 0.
            </MetricCard>
            <MetricCard eyebrow="Perfect knowledge is worth" value={`${fmt(r.evpi)} ${u}`}>
              The most any study could add. No method can beat it.
            </MetricCard>
          </MetricGrid>
          <DataTable<Any>
            className="mt-6"
            caption="Each way to learn"
            rows={[...r.rows].sort((a: Any, b: Any) => b.net - a.net)}
            rowKey={(x) => x.index}
            columns={[
              { head: "Method", cell: (x) => x.label },
              { head: `Expected gain (${u})`, numeric: true, cell: (x) => fmt(x.evsi) },
              { head: `Cost (${u})`, numeric: true, cell: (x) => fmt(x.cost) },
              { head: `Net (${u})`, numeric: true, cell: (x) => <span className={x.net > 0 ? "font-bold" : "text-muted-foreground"}>{fmt(x.net)}</span> },
              { head: "Share of perfect", numeric: true, cell: (x) => pct(x.share) },
            ]}
          />
          <DataTable<Any>
            className="mt-6"
            caption="Most each method could ever give, with unlimited data"
            rows={r.rows}
            rowKey={(x) => x.index}
            columns={[
              { head: "Method", cell: (x) => x.label },
              { head: `Ceiling (${u})`, numeric: true, cell: (x) => fmt(x.ceiling) },
              { head: "Ceiling as share of perfect", numeric: true, cell: (x) => pct(r.evpi > 0 ? x.ceiling / r.evpi : 0) },
            ]}
          />
          <p className="mb-0 mt-2 text-sm text-muted-foreground">
            Bias caps a method: past the ceiling, more interviews or a longer before/after add nothing.
          </p>
          <BriefLines className="mt-6" lines={summaryLines(r, state)} />
          <OpenInTool
            title="Plan the research"
            targets={[
              {
                id: "learnbacklog",
                name: "Learning Backlog",
                use: hub
                  ? "Return to your learning backlog with this decision added, using the best method above."
                  : "Rank this decision against your other open questions, using the best method above.",
                href: backlogHref,
                reason: "Add a way to learn first.",
              },
            ]}
            note="The decision's name, value range and the chosen method travel."
          />
        </Panel>
      )}
    </BoldPanelLayout>
  );
}
